// @flow
import * as React from 'react';

import Logging from '@ta/common.logging';
import { SocialLoginGate } from '@ta/social.login-gate';
import { Toaster, type ToastType } from '@ta/overlays.toast';
import classNames from '@ta/common.classnames';
import { localize, unescaped } from '@ta/common.i18n';
import { EscapeHtml } from '@ta/common.text';
import { updateMemberData, getUpdatedMember } from '@ta/social.member-common';
import { useMutation } from 'urql';
import {
  InteractionLogger,
  FollowTabTypeContext,
  FollowPlacementContext,
  RecommendedFolloweesImpressionIdContext,
} from '@ta/tracking.interactions';

import styles from './FollowAction.less';
import Follow from './AddFollow.gql';
import Unfollow from './DeleteFollow.gql';

export type Profile = $ReadOnly<{
  userId: string,
  isFollowing: boolean,
  followerCount?: number,
  followeeCount?: number,
  displayName: string,
  isVerified: boolean,
}>;

export type GatedFollowAction = () => Promise<*>;

type Props = {|
  profile: ?Profile,
  disableToast?: boolean,
  children: (action: GatedFollowAction) => React.Node,
|};

const blockedToast: ToastType = {
  node: localize('social_no_permission'),
};

const FollowAction = ({ profile, disableToast, children }: Props) => {
  const followTabType = React.useContext(FollowTabTypeContext);
  const followContext = React.useContext(FollowPlacementContext);
  const impressionIdContext = React.useContext(RecommendedFolloweesImpressionIdContext);

  const [, followMutation] = useMutation(Follow);
  const [, unfollowMutation] = useMutation(Unfollow);
  if (!profile) {
    Logging.error('Expected non-null profile in FollowAction');
    return children(() => Promise.resolve());
  }
  return (
    <InteractionLogger>
      {logInteraction => (
        <SocialLoginGate>
          {(socialGate, loggedInUserId) => (
            <Toaster>
              {launchToast => {
                const pid = 40473;
                const logFollow = (interactionType, userId) => {
                  const trackingParams: Object = { followeeId: userId };
                  if (followContext) {
                    Object.assign(trackingParams, followContext);
                  }
                  if (followTabType) {
                    Object.assign(trackingParams, followTabType);
                  }
                  if (impressionIdContext) {
                    Object.assign(trackingParams, impressionIdContext);
                  }
                  // Following something really shouldn't ever navigate you away from the page
                  logInteraction(null, interactionType, trackingParams);
                };

                const gatedAction = () => {
                  const updatedProfile = getUpdatedMember(profile);
                  const logInteractionType = updatedProfile.isFollowing ? 'unfollow' : 'follow';
                  logFollow(logInteractionType, loggedInUserId);
                  if (logInteractionType === 'unfollow') {
                    return socialGate(pid).then(() =>
                      unfollowMutation({ objectType: 'MEMBER', objectId: profile.userId }).then(
                        result => {
                          if (!result.error && !disableToast) {
                            if (result?.data?.deleteFollow?.statusType === 'NO_PERMISSION') {
                              launchToast(blockedToast);
                            } else {
                              updateMemberData(profile.userId, prof => ({
                                isFollowing: false,
                                followerCount: (prof.followerCount || 0) - 1,
                              }));
                              updateMemberData(loggedInUserId, prof => ({
                                followeeCount: (prof.followeeCount || 0) - 1,
                              }));
                            }
                          }
                        },
                      ),
                    );
                  }
                  return socialGate(pid).then(() =>
                    followMutation({ objectType: 'MEMBER', objectId: profile.userId }).then(
                      result => {
                        if (!result.error && !disableToast) {
                          if (result?.data?.addFollow?.statusType === 'NO_PERMISSION') {
                            launchToast(blockedToast);
                          } else {
                            launchToast({
                              iconName: 'add-friend-fill',
                              node: unescaped(
                                localize('social_follow_toast_message', {
                                  sDisplayNameClass: classNames(styles.followee_display_name, {
                                    [styles.verified]: profile.isVerified,
                                  }),
                                  sDisplayName: EscapeHtml(profile.displayName),
                                }),
                              ),
                            });
                            updateMemberData(profile.userId, prof => ({
                              isFollowing: true,
                              followerCount: (prof.followerCount || 0) + 1,
                            }));
                            updateMemberData(loggedInUserId, prof => ({
                              followeeCount: (prof.followeeCount || 0) + 1,
                            }));
                          }
                        }
                      },
                    ),
                  );
                };
                return children(gatedAction);
              }}
            </Toaster>
          )}
        </SocialLoginGate>
      )}
    </InteractionLogger>
  );
};

export default FollowAction;
